// ---------------------------------------------------------------------------
// User-uploaded reference layers (GenericLayersPanel.tsx / SubBasinMap.tsx):
// purely visual — a point/line/polygon shapefile or GeoJSON shown on the map
// for reference, with no domain meaning (not linked to any sub-basin, outlet
// point or aquifer, no role in the calculation). Deliberately NOT part of
// EvalHidProject (types.ts): unlike gisLayer/outletPointsLayer/aquifersLayer,
// there's nothing here for the calculation engine to ever read, so it lives
// at the same level as those three in App.tsx's own State (appState.ts)
// instead — see that file's own comment.
// ---------------------------------------------------------------------------
import { TranslatableError } from "../i18n/translate.ts";
import { PALETTE } from "./assignmentColors.ts";

export type GenericLayerGeometryKind = "point" | "line" | "polygon";

/**
 * Every field applies to some geometry kinds and not others (`radius` only
 * makes sense for points, `fillOpacity` only for polygons) — kept as one
 * flat shape anyway rather than a kind-specific union, so switching a
 * layer's style controls doesn't need a runtime type migration; the UI
 * (GenericLayersPanel.tsx) only shows the fields that apply to that layer's
 * own `geometryKind`.
 */
export interface GenericLayerStyle {
  color: string;
  weight: number;
  fillOpacity: number;
  radius: number;
}

export interface GenericLayer {
  id: string;
  name: string;
  /** Derived once from the first feature's geometry at upload time (see `geometryKindOf`) — a shapefile/GeoJSON never mixes point/line/polygon features in practice, so one style set per layer is enough. */
  geometryKind: GenericLayerGeometryKind;
  geojson: GeoJSON.FeatureCollection;
  style: GenericLayerStyle;
  /** Shown on the map's own LayersControl overlay list (SubBasinMap.tsx) — kept here, not just in Leaflet's internal checkbox state, so GenericLayersPanel.tsx's own toggle stays in sync with it. */
  visible: boolean;
}

const POINT_TYPES = new Set(["Point", "MultiPoint"]);
const LINE_TYPES = new Set(["LineString", "MultiLineString"]);
const POLYGON_TYPES = new Set(["Polygon", "MultiPolygon"]);

/** `null` for an empty FeatureCollection or a geometry type this app has no style controls for (GeometryCollection). */
export function geometryKindOf(
  geojson: GeoJSON.FeatureCollection
): GenericLayerGeometryKind | null {
  const type = geojson.features[0]?.geometry?.type;
  if (!type) return null;
  if (POINT_TYPES.has(type)) return "point";
  if (LINE_TYPES.has(type)) return "line";
  if (POLYGON_TYPES.has(type)) return "polygon";
  return null;
}

function defaultStyle(color: string): GenericLayerStyle {
  return { color, weight: 2, fillOpacity: 0.3, radius: 6 };
}

/**
 * Adds a new layer, already-uploaded GeoJSON in hand (see `loadGisFiles`,
 * gisUpload.ts — reused as-is, no column mapping needed here unlike
 * sub-basins/outlet points/aquifers: nothing to link). The default color
 * cycles through the same qualitative palette as assignmentColors.ts, so a
 * few layers uploaded in a row still read as visually distinct without the
 * user having to pick colors by hand right away.
 */
export function createGenericLayer(
  existing: GenericLayer[],
  name: string,
  geojson: GeoJSON.FeatureCollection
): GenericLayer[] {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("genericLayer.emptyName");
  const geometryKind = geometryKindOf(geojson);
  if (!geometryKind) throw new TranslatableError("genericLayer.unsupportedGeometry");
  const layer: GenericLayer = {
    id: crypto.randomUUID(),
    name: cleanName,
    geometryKind,
    geojson,
    style: defaultStyle(PALETTE[existing.length % PALETTE.length]),
    visible: true,
  };
  return [...existing, layer];
}

export function renameGenericLayer(
  existing: GenericLayer[],
  id: string,
  name: string
): GenericLayer[] {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("genericLayer.emptyName");
  return existing.map((l) => (l.id === id ? { ...l, name: cleanName } : l));
}

/** Permanent — no undo, same as every other delete in this app (see ConfirmDeleteDialog.tsx). Nothing else references a generic layer by id, so unlike deleteZone/deleteOutletPoint there's no cascade to worry about. */
export function deleteGenericLayer(existing: GenericLayer[], id: string): GenericLayer[] {
  return existing.filter((l) => l.id !== id);
}

export function toggleGenericLayerVisible(existing: GenericLayer[], id: string): GenericLayer[] {
  return existing.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l));
}

export function updateGenericLayerStyle(
  existing: GenericLayer[],
  id: string,
  style: Partial<GenericLayerStyle>
): GenericLayer[] {
  return existing.map((l) => (l.id === id ? { ...l, style: { ...l.style, ...style } } : l));
}
